import React, { useState, useCallback, useRef } from "react";
import { useFormContext } from "react-hook-form";
import TextInput from "./TextInput";
import { validationMode } from "../../config/helpers";

const IGN_GEOCODE_URL = "https://data.geopf.fr/geocodage/search";

interface GeoFeature {
  properties: {
    label: string;
    name: string;
    postcode: string;
    city: string;
  };
}

interface AddressAutocompleteProps {
  disabled?: boolean;
  ismandatory?: boolean;
  validationStyle?: validationMode;
  fieldNames?: {
    adresse?: string;
    codePostal?: string;
    ville?: string;
  };
}

const AddressAutocomplete: React.FC<AddressAutocompleteProps> = ({
  disabled = false,
  ismandatory = false,
  validationStyle = validationMode.classic,
  fieldNames = {},
}) => {
  const { setValue } = useFormContext();
  const {
    adresse: fieldAdresse = "Adresse",
    codePostal: fieldCodePostal = "CodePostal",
    ville: fieldVille = "Ville",
  } = fieldNames;

  const [suggestions, setSuggestions] = useState<GeoFeature[]>([]);
  const [isGeocoding, setIsGeocoding] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const fetchSuggestions = useCallback(async (adresse: string) => {
    const q = adresse.trim();
    if (q.length < 3) {
      setSuggestions([]);
      return;
    }
    setIsGeocoding(true);
    try {
      const params = new URLSearchParams({ q, limit: "5" });
      const res = await fetch(`${IGN_GEOCODE_URL}?${params}`);
      if (!res.ok) {
        throw new Error();
      }
      const data = await res.json();
      setSuggestions(data.features || []);
    } catch {
      setSuggestions([]);
    } finally {
      setIsGeocoding(false);
    }
  }, []);

  const handleAddressChange = (value: string) => {
    setValue(fieldAdresse, value, { shouldValidate: true, shouldTouch: true });
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    debounceRef.current = setTimeout(() => fetchSuggestions(value), 400);
  };

  const handleSelectSuggestion = (feature: GeoFeature) => {
    const { name, postcode, city } = feature.properties;
    setValue(fieldAdresse, name, { shouldValidate: true, shouldTouch: true });
    setValue(fieldCodePostal, postcode, { shouldValidate: true, shouldTouch: true });
    setValue(fieldVille, city, { shouldValidate: true, shouldTouch: true });
    setSuggestions([]);
  };

  return (
    <div style={{ position: "relative" }}>
      <TextInput
        label="Adresse"
        formName={fieldAdresse}
        ismandatory={ismandatory}
        validationStyle={validationStyle}
        placeholder="Adresse"
        disabled={disabled}
        onchange={!disabled ? handleAddressChange : undefined}
      />
      {isGeocoding && (
        <small style={{ position: "absolute", right: 8, top: 8, color: "#999" }}>
          Recherche...
        </small>
      )}
      {suggestions.length > 0 && (
        <ul style={dropdownStyle}>
          {suggestions.map((feature, index) => (
            <li
              key={index}
              style={dropdownItemStyle}
              onMouseDown={() => handleSelectSuggestion(feature)}
              onMouseEnter={(e) => (e.currentTarget.style.background = "#f0f0f0")}
              onMouseLeave={(e) => (e.currentTarget.style.background = "#fff")}
            >
              {feature.properties.label}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

const dropdownStyle: React.CSSProperties = {
  position: "absolute",
  zIndex: 1000,
  top: "100%",
  left: 0,
  right: 0,
  background: "#fff",
  border: "1px solid #ccc",
  borderRadius: "4px",
  margin: 0,
  padding: 0,
  listStyle: "none",
  boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
  maxHeight: "220px",
  overflowY: "auto",
};

const dropdownItemStyle: React.CSSProperties = {
  padding: "8px 12px",
  cursor: "pointer",
  fontSize: "0.9em",
  borderBottom: "1px solid #eee",
  background: "#fff",
};

export default AddressAutocomplete;
